"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Phone, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function Farnborough() {
  const shouldReduceMotion = useReducedMotion();
  const services = [{
    name: "Commercial & Site Access",
    desc: "Main contractors and developers"
  }, {
    name: "Residential & Domestic",
    desc: "Extensions, renovations and paintwork"
  }, {
    name: "Roofing & Solar Access",
    desc: "Dedicated temporary roofs and solar installs"
  }, {
    name: "Specialist & Engineered",
    desc: "Complex cantilevers, bridges and tight access"
  }];
  const accreditations = ["NASC Qualified", "CHAS Accredited"];
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="farnboroughhero" data-nav="dark" className="relative overflow-hidden bg-secondary text-secondary-foreground py-20 lg:py-28">
      {/* Background Architectural Accent & Grid */}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--color-primary)_0%,transparent_60%)]" />
        <div className="h-full w-full bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Main Hero Copy */}
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="lg:col-span-7 space-y-6">
            {/* Accreditation / Location Badge */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="bg-secondary-foreground/10 text-secondary-foreground border-secondary-foreground/20 px-3 py-1 text-xs font-semibold tracking-wide uppercase">
                <ShieldCheck className="size-3.5 text-primary-foreground mr-1" />
                Farnborough & Surrounding Areas
              </Badge>
            </div>

            {/* H1 Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-secondary-foreground leading-tight">
              Scaffolding in Farnborough
            </h1>

            {/* Proposition */}
            <p className="text-lg sm:text-xl text-secondary-foreground/85 leading-relaxed max-w-2xl">
              BMF Scaffolding provides commercial, residential, roofing, solar and specialist scaffolding in Farnborough and the surrounding Hampshire area, including GU14.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-md">
                <Link to="/ContactRequestQuotePage">
                  Request a quote
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-secondary-foreground/30 bg-secondary text-secondary-foreground hover:bg-secondary-foreground/10 hover:text-secondary-foreground font-semibold shadow-xs">
                <a href="tel:01344929900" className="inline-flex items-center text-secondary-foreground hover:text-secondary-foreground">
                  <Phone className="size-4 mr-2" />
                  Call 01344 929900
                </a>
              </Button>
            </div>

            {/* Trust Markers */}
            <div className="pt-4 border-t border-secondary-foreground/15 flex flex-wrap items-center gap-y-2 gap-x-6 text-sm text-secondary-foreground/75">
              {accreditations.map((item, i) => <div key={item} data-index={i} className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-chart-4" />
                  <span>{item}</span>
                </div>)}
            </div>
          </motion.div>

          {/* Service Matrix Highlights */}
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="lg:col-span-5">
            <div className="rounded-xl border border-secondary-foreground/15 bg-secondary-foreground/5 p-6 sm:p-8 backdrop-blur-sm shadow-xl space-y-4">
              <div className="border-b border-secondary-foreground/10 pb-3">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-secondary-foreground/70">
                  Scope of Scaffolding Services
                </h2>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {services.map((svc, i) => <div key={svc.name} data-index={i} className="p-3.5 rounded-lg bg-secondary/80 border border-secondary-foreground/10 hover:border-secondary-foreground/25 transition-[border-color,background-color] duration-200">
                    <h3 className="font-semibold text-secondary-foreground text-sm flex items-center justify-between">
                      {svc.name}
                      <span className="text-xs text-secondary-foreground/50">Farnborough</span>
                    </h3>
                    <p className="text-xs text-secondary-foreground/75 mt-1">
                      {svc.desc}
                    </p>
                  </div>)}
              </div>

              <div className="pt-2 text-xs text-secondary-foreground/60 text-center">
                Fully managed design, erection, ongoing inspections, and dismantling.
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}